import { CheckSquare, FileIcon, FileText, LoaderIcon, Maximize2Icon, MoreHorizontalIcon, PlusIcon, TargetIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  useDropdownMenuSubHoverDelay,
} from "@/components/ui/dropdown-menu";
import { useTranslate } from "@/utils/i18n";
import { useFileUpload } from "../hooks";
import { useEditorContext } from "../state";
import type { InsertMenuProps } from "../types";
import type { LocalFile } from "../types/attachment";

const InsertMenu = (props: InsertMenuProps) => {
  const t = useTranslate();
  const { actions, dispatch } = useEditorContext();

  const [moreSubmenuOpen, setMoreSubmenuOpen] = useState(false);

  const { handleTriggerEnter, handleTriggerLeave, handleContentEnter, handleContentLeave } = useDropdownMenuSubHoverDelay(
    150,
    setMoreSubmenuOpen,
  );

  const { fileInputRef, selectingFlag, handleFileInputChange, handleUploadClick } = useFileUpload((newFiles: LocalFile[]) => {
    newFiles.forEach((file) => dispatch(actions.addLocalFile(file)));
  });

  const isUploading = selectingFlag || props.isUploading;

  const handleInsertSelfContract = () => {
    props.onInsertSelfContract?.();
  };

  const handleInsertDecision = () => {
    props.onInsertDecision?.();
  };

  const handleInsertChecklist = () => {
    props.onInsertChecklist?.();
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="shadow-none" disabled={isUploading}>
            {isUploading ? <LoaderIcon className="size-4 animate-spin" /> : <PlusIcon className="size-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={handleUploadClick}>
            <FileIcon className="w-4 h-4" />
            {t("common.upload")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleInsertDecision}>
            <FileText className="w-4 h-4" />
            {t("editor.decision" as any)}
            <span className="ml-auto text-xs text-muted-foreground opacity-60">/decision</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleInsertSelfContract}>
            <TargetIcon className="w-4 h-4" />
            {t("editor.selfContract" as any)}
            <span className="ml-auto text-xs text-muted-foreground opacity-60">/contract</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleInsertChecklist}>
            <CheckSquare className="w-4 h-4" />
            {t("editor.checklist" as any)}
            <span className="ml-auto text-xs text-muted-foreground opacity-60">/checklist</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {/* View submenu with Focus Mode */}
          <DropdownMenuSub open={moreSubmenuOpen} onOpenChange={setMoreSubmenuOpen}>
            <DropdownMenuSubTrigger onPointerEnter={handleTriggerEnter} onPointerLeave={handleTriggerLeave}>
              <MoreHorizontalIcon className="w-4 h-4" />
              {t("common.more")}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent onPointerEnter={handleContentEnter} onPointerLeave={handleContentLeave}>
              <DropdownMenuItem
                onClick={() => {
                  props.onToggleFocusMode?.();
                  setMoreSubmenuOpen(false);
                }}
              >
                <Maximize2Icon className="w-4 h-4" />
                {t("editor.focus-mode")}
                <span className="ml-auto text-xs text-muted-foreground opacity-60">⌘⇧F</span>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <div className="px-2 py-1 text-xs text-muted-foreground opacity-80">{t("editor.slash-commands")}</div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Hidden file input */}
      <input
        className="hidden"
        ref={fileInputRef}
        disabled={isUploading}
        onChange={handleFileInputChange}
        type="file"
        multiple={true}
        accept="*"
      />
    </>
  );
};

export default InsertMenu;
